import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Typography as MuiTypography,
  Grid as MuiGrid,
  Divider as MuiDivider,
  Button as MuiButton,
  TextField as MuiTextField,
} from "@mui/material";
import { Loop as MuiLoopIcon } from "@mui/icons-material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { useProspectContext } from "app/services/prospectContext";
import { ProspectDefaults } from "app/shared/constants";
import { encodeBase64 } from "app/shared/utils";
import { Autocomplete } from "app/shared/UI/Autocomplete/Autocomplete";

const prospectFiltersStyles = {
  headerMargin: { margin: "20px 25px 20px 25px" },
  contentMargin: { margin: "0px 25px 20px 25px" },
};

const ProspectFilters = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    filters,
    setFilters,
    setInitialLoad,
    setSort,
    setPagination,
    pagination,
  } = useProspectContext();

  // Controllable State - First Name
  const [firstName, setFirstName] = useState(filters.firstName);

  // Controllable State - Last Name
  const [lastName, setLastName] = useState(filters.lastName);

  // Controllable State - Phone
  const [phone, setPhone] = useState(filters.phone);

  // Controllable State - Email
  const [email, setEmail] = useState(filters.email);

  // Controllable State - MRN
  const [mrn, setMRN] = useState(filters.mrn);

  const [emailError, setEmailError] = useState(false);

  const [birthDate, setBirthDate] = useState(filters.birthDate);

  const [isOptedOut, setIsOptedOut] = useState(filters.isOptedOut);
  const [isHavingConcern, setIsHavingConcern] = useState(
    filters.isHavingConcern
  );

  const handleResetProspectFilters = () => {
    // reset filters to default one
    setFilters({
      firstName: null,
      lastName: null,
      phone: null,
      email: null,
      mrn: null,
      birthDate: null,
      isOptedOut: null,
      isHavingConcern: null,
    });
    setFirstName("");
    setLastName("");
    setMRN("");
    setPhone("");
    setEmail("");
    setBirthDate(null);
    setIsOptedOut(null);
    setIsHavingConcern(null);
    setInitialLoad(true);
    setSort({
      orderBy: ProspectDefaults.PROSPECTDEFAULTORDERBY,
      order: ProspectDefaults.PROSPECTDEFAULTORDER,
    });
    setPagination({
      page: ProspectDefaults.PROSPECTDEFAULTPAGE,
      rowsPerPage: ProspectDefaults.PROSPECTDEFAULTPAGESIZE,
    });
    setEmailError(false);
  };

  const handleApplyLookupFilters = () => {
    setFilters({
      firstName: (firstName && firstName.trim()) || null,
      lastName: (lastName && lastName.trim()) || null,
      phone: (phone && phone.trim()) || null,
      email: (email && email.trim()) || null,
      mrn: (mrn && mrn.trim()) || null,
      birthDate: birthDate || null,
      isOptedOut: isOptedOut,
      isHavingConcern: isHavingConcern,
    });
    setInitialLoad(false);
    setPagination({
      ...pagination,
      page: 0,
    });
  };

  useEffect(() => {
    const params = {
      firstName,
      lastName,
      phone,
      email,
      mrn,
      birthDate,
      isOptedOut,
      isHavingConcern,
    };
    const queryParams = Object.keys(params)
      .filter((key) => params[key])
      .map((key) => {
        return `${key}=${encodeBase64(params[key])}`;
      })
      .join("&");
    navigate(`${location.pathname}?${queryParams}`);
  }, [
    firstName,
    lastName,
    phone,
    email,
    mrn,
    navigate,
    birthDate,
    isOptedOut,
    isHavingConcern,
    location.pathname,
  ]);

  return (
    <MuiGrid container>
      <MuiGrid size={12} sx={prospectFiltersStyles.headerMargin}>
        <MuiTypography variant="h6" noWrap>
          {t("ProspectFilters.title")}
        </MuiTypography>
      </MuiGrid>
      <MuiGrid size={12}>
        <MuiDivider />
      </MuiGrid>
      <MuiGrid
        size={12}
        sx={prospectFiltersStyles.headerMargin}
        container
        justifyContent="flex-end"
      >
        <MuiGrid size={11} container justifyContent="space-between">
          <MuiGrid size={8}>
            <MuiButton
              startIcon={<MuiLoopIcon fontSize="small" />}
              onClick={handleResetProspectFilters}
            >
              {t("formLabel.buttonResetSearchCriteria")}
            </MuiButton>
          </MuiGrid>
          <MuiGrid size={3}>
            <MuiButton
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleApplyLookupFilters}
              disabled={emailError}
            >
              {t("formLabel.buttonSearch")}
            </MuiButton>
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        {/* MRN Number - MuiTextField Input */}
        <MuiTextField
          id="mrnNumber"
          label={t("LookupProspect.tableColumnMRN")}
          variant="outlined"
          margin="dense"
          size="small"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={t("LookupProspect.tableColumnMRN")}
          fullWidth
          autoComplete="off"
          value={mrn || ""}
          onChange={(event) => {
            setMRN(event.target.value);
          }}
        />
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        {/* First Name - MuiTextField Input */}
        <MuiTextField
          id="firstName"
          label={t("LookupProspect.tableColumnFirstName")}
          variant="outlined"
          margin="dense"
          size="small"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={t("LookupProspect.tableColumnFirstName")}
          fullWidth
          autoComplete="off"
          value={firstName || ""}
          onChange={(event) => {
            setFirstName(event.target.value);
          }}
        />
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        {/* Last Name - MuiTextField Input */}
        <MuiTextField
          id="lastName"
          label={t("LookupProspect.tableColumnLastName")}
          variant="outlined"
          margin="dense"
          size="small"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={t("LookupProspect.tableColumnLastName")}
          fullWidth
          autoComplete="off"
          value={lastName || ""}
          onChange={(event) => {
            setLastName(event.target.value);
          }}
        />
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        {/* Phone - MuiTextField Input */}
        <MuiTextField
          id="phone"
          label={t("LookupProspect.tableColumnPhone")}
          variant="outlined"
          margin="dense"
          size="small"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={t("LookupProspect.tableColumnPhone")}
          fullWidth
          value={phone || ""}
          onChange={(event) => {
            setPhone(event.target.value);
          }}
          autoComplete="off"
        />
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        {/* Email - MuiTextField Input */}
        <MuiTextField
          id="email"
          label={t("LookupProspect.tableColumnEmail")}
          variant="outlined"
          margin="dense"
          size="small"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={t("LookupProspect.tableColumnEmail")}
          fullWidth
          autoComplete="off"
          value={email || ""}
          onChange={(event) => {
            const pattern = new RegExp(
              "^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+.[a-zA-Z]$"
            );
            setEmailError(false);
            if (!pattern.test(event.target.value)) {
              setEmailError(true);
              !event.target.value && setEmailError(false);
            }
            setEmail(event.target.value);
          }}
          error={emailError}
          helperText={emailError && t("ProspectFilters.invalidEmailAddressMsg")}
        />
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
          <MuiDatePicker
            margin="dense"
            label={t("LookupProspect.tableColumnDOB")}
            placeholder={t("LookupProspect.tableColumnDOB")}
            format="MM/dd/yyyy"
            value={birthDate}
            onChange={(date) => {
              if (date === null || !isNaN(date.getTime())) {
                setBirthDate(date);
              }
            }}
            fullWidth
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                InputLabelProps: { shrink: true },
              },
            }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
            variant="inline"
            inputVariant="outlined"
            sx={{ width: "100%" }}
          />
        </MuiLocalizationProvider>
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        <Autocomplete
          id="opt-out"
          key="optedOut"
          label={t(`LookupProspect.tableColumnOptOut`)}
          variant="outlined"
          margin="normal"
          size="small"
          options={["Yes"]}
          getOptionLabel={(option) => option}
          value={isOptedOut ? "Yes" : ""}
          onChange={(evt, newValue) => {
            setIsOptedOut(newValue === "Yes" ? true : "");
          }}
          clientSide={true}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      </MuiGrid>
      <MuiGrid size={12} sx={prospectFiltersStyles.contentMargin}>
        <Autocomplete
          id="opt-out"
          key="optedOut"
          label={t(`LookupProspect.tableColumnConcernOnFile`)}
          variant="outlined"
          margin="normal"
          size="small"
          options={["Yes"]}
          getOptionLabel={(option) => option}
          value={isHavingConcern ? "Yes" : ""}
          onChange={(evt, newValue) => {
            setIsHavingConcern(newValue === "Yes" ? true : "");
          }}
          clientSide={true}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      </MuiGrid>
    </MuiGrid>
  );
};

export default ProspectFilters;
